<template>
    <div
        v-if="availStatusObj.statusText"
        class="d-flex align-center"
        :class="direction === 'horizontal' ? 'justify-space-between' : 'flex-column justify-center'"
    >
        <v-chip
            :color="useGetStatusColor(availStatusObj.statusText)"
            variant="outlined"
            :density="(direction === 'vertical' && !showEstAvailableDate) ? 'default' : 'compact'"
            :size="size"
            label
        >
            {{ availStatusObj.statusText }}
        </v-chip>
        <span
            v-if="showEstAvailableDate"
            :class="size === 'small' ? 'font-size-8' : ''"
        >
            <span v-if="!compact">Estimated </span><strong>{{ statusObj.availableDate }}</strong>
        </span>
    </div>
</template>

<script lang="ts" setup>
    import type {
        GenericObject
    } from '~/types';
    import {
        ProductAvailabilityStatus
    } from '~/types';
    import {
        useGetStatusColor,
        useProductAvailabilityStatus
    } from '~/composables';
    import {
        isToday
    } from 'x1-base/utils';

    interface LocalStatusObj {
        status: Nullable<string>;
        availableDate: Nullable<string>;
    }

    interface ObjectWithStatus extends GenericObject<any> {
        status: Nullable<string>;
    };

    interface ObjectWithStatusAndAvailableDate extends ObjectWithStatus {
        canAddToCart: boolean;
        availableDate: Nullable<string>;
    };

    const props = withDefaults(
        defineProps<{
            currentSku: ObjectWithStatus;
            augmentStatus?: Nilish<ObjectWithStatusAndAvailableDate[]>;
            size?: Undefinable<string | number>;
            direction?: 'horizontal' | 'vertical';
            compact?: boolean;
        }>(),
        {
            size: 'x-large',
            direction: 'horizontal'
        }
    );

    const statusObj: LocalStatusObj = reactive({
        status: props.currentSku.status,
        availableDate: props.currentSku.availableDate
    });

    const availStatusObj = reactive({
        statusText: null,
        canShowEstAvailableDate: false
    });

    const showEstAvailableDate = computed<boolean>(() => {
        if (!statusObj.availableDate) {
            return false;
        }

        return availStatusObj.canShowEstAvailableDate;
    });

    function setStatusObj(): void {
        const obj = useProductAvailabilityStatus(statusObj.status as ProductAvailabilityStatus);
        Object.assign(availStatusObj, obj);
    }

    function statusUpdateHandler(
        newStatus: Nilish<ObjectWithStatusAndAvailableDate[]>
    ): void {
        if (!newStatus?.length) {
            setDefaultStatusAndAvailableDate();
            return;
        }

        const canAddToCart = new Set<boolean>();
        const availableDates = new Set<string>();
        const sorted = newStatus.slice(0).sort((a, b) => {
            canAddToCart.add(a.canAddToCart);
            canAddToCart.add(b.canAddToCart);
            if (a.availableDate && !isToday(a.availableDate)) availableDates.add(a.availableDate);
            if (b.availableDate && !isToday(b.availableDate)) availableDates.add(b.availableDate);

            const d1 = a.availableDate, d2 = b.availableDate;
            if (!d1) return -1;
            if (!d2) return 1;
            if (d1 < d2) return -1;
            if (d1 > d2) return 1;
            return 0;
        });

        if (!availableDates.size && !canAddToCart.has(false)) {
            setDefaultStatusAndAvailableDate();
            return;
        }

        const items = sorted.reverse();
        const latestShipObj = items.find((o, i) => {
            const { availableDate, status } = o;

            if (availableDate && status) {
                const nextItem = items[i + 1];

                if (status !== 'preorder') return o;

                if (nextItem) {
                    const { availableDate: nextAvailableDate, status: nextStatus } = nextItem;

                    if (nextAvailableDate && nextStatus && availableDate === nextAvailableDate && status !== nextStatus) {
                        return;
                    }

                    return o;
                }
            }
        });

        if (latestShipObj) {
            const { availableDate, status } = latestShipObj;
            statusObj.availableDate = availableDate;
            statusObj.status = status;
            return;
        }

        setDefaultStatusAndAvailableDate();
    }

    function setDefaultStatusAndAvailableDate(): void {
        const newSku = props.currentSku;
        statusObj.status = newSku?.status || null;
        statusObj.availableDate = newSku?.availableDate || null;
    }

    const unWatchSku = watch(
        () => props.currentSku,
        (newSku) => {
            setDefaultStatusAndAvailableDate();
            setStatusObj();
        },
        {
            immediate: true
        }
    );

    const unWatchStatusObj = watch(
        statusObj,
        setStatusObj,
        {
            immediate: true,
            deep: true
        }
    );

    const unWatchAugStatus = watch(
        () => props.augmentStatus,
        statusUpdateHandler,
        {
            immediate: true,
            deep: true
        }
    );

    onBeforeUnmount(() => {
        unWatchStatusObj();
        unWatchSku();
        unWatchAugStatus();
    });
</script>

<style lang="scss"></style>
